/**
 * StrengthBreakdown — Core 5 lift e1RM breakdown for a client.
 *
 * Shows each Core 5 lift with the estimated 1RM and how it contributes
 * to the total strength score. Reads from strength_events / baseline_lifts.
 *
 * Zero effect on scoring/matchmaking — read-only display (doctrine compliant).
 */

import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { supabase } from '@/lib/supabase';
import { Dumbbell } from 'lucide-react-native';

const CORE_5 = [
  { key: 'squat', label: 'Squat' },
  { key: 'bench_press', label: 'Bench Press' },
  { key: 'deadlift', label: 'Deadlift' },
  { key: 'overhead_press', label: 'OHP' },
  { key: 'barbell_row', label: 'Row' },
] as const;

interface LiftData {
  key: string;
  label: string;
  e1rm: number;
  lastUpdated: string | null;
}

interface Props {
  clientId: string;
}

export default function StrengthBreakdown({ clientId }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [lifts, setLifts] = useState<LiftData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalScore, setTotalScore] = useState(0);

  useEffect(() => {
    loadBreakdown();
  }, [clientId]);

  const loadBreakdown = async () => {
    setLoading(true);
    try {
      // Try baseline_lifts first (the canonical e1RM source)
      const { data: baselineData } = await supabase
        .from('baseline_lifts')
        .select('exercise_key, e1rm, updated_at')
        .eq('profile_id', clientId);

      const baselineMap = new Map(
        (baselineData ?? []).map((b: any) => [b.exercise_key, b]),
      );

      // Fallback to strength_events for lifts not in baseline
      const { data: eventsData } = await supabase
        .from('strength_events')
        .select('lift_key, estimated_e1rm, created_at')
        .eq('profile_id', clientId)
        .in('lift_key', CORE_5.map((l) => l.key))
        .order('created_at', { ascending: false });

      const eventsMap = new Map<string, any>();
      for (const e of eventsData ?? []) {
        if (!eventsMap.has(e.lift_key)) {
          eventsMap.set(e.lift_key, e);
        }
      }

      let total = 0;
      const results: LiftData[] = CORE_5.map((lift) => {
        const bl = baselineMap.get(lift.key);
        const ev = eventsMap.get(lift.key);
        const e1rm = bl?.e1rm ?? ev?.estimated_e1rm ?? 0;
        const lastUpdated = bl?.updated_at ?? ev?.created_at ?? null;
        total += e1rm;
        return { key: lift.key, label: lift.label, e1rm, lastUpdated };
      });

      setLifts(results);
      setTotalScore(total);
    } catch (err) {
      console.warn('[StrengthBreakdown] load error', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ActivityIndicator color={c.accent} style={{ marginVertical: 16 }} />;
  }

  const maxE1rm = Math.max(...lifts.map((l) => l.e1rm), 1);

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.headerRow}>
        <Dumbbell size={16} color={c.accent} />
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
          CORE 5 BREAKDOWN
        </Text>
        <Text style={[styles.totalScore, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
          {Math.round(totalScore)}
        </Text>
      </View>

      {lifts.map((lift) => {
        const pct = maxE1rm > 0 ? (lift.e1rm / maxE1rm) * 100 : 0;
        return (
          <View key={lift.key} style={styles.liftRow}>
            <Text style={[styles.liftLabel, { color: c.textSecondary }]}>
              {lift.label}
            </Text>
            <View style={[styles.barTrack, { backgroundColor: c.border }]}>
              <View
                style={[
                  styles.barFill,
                  {
                    backgroundColor: lift.e1rm > 0 ? c.accent : 'transparent',
                    width: `${Math.round(pct)}%`,
                  },
                ]}
              />
            </View>
            <Text style={[styles.e1rmValue, { color: lift.e1rm > 0 ? c.text : c.textTertiary }]}>
              {lift.e1rm > 0 ? `${Math.round(lift.e1rm)}` : '—'}
            </Text>
          </View>
        );
      })}

      {lifts.every((l) => l.e1rm === 0) && (
        <Text style={[styles.emptyHint, { color: c.textTertiary }]}>
          No baseline lifts recorded yet.
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    flex: 1,
    fontSize: 13,
    letterSpacing: 1,
  },
  totalScore: {
    fontSize: 18,
  },
  liftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  liftLabel: {
    width: 80,
    fontSize: 12,
    fontWeight: '600',
  },
  barTrack: {
    flex: 1,
    height: 14,
    borderRadius: 2,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 2,
  },
  e1rmValue: {
    width: 40,
    fontSize: 13,
    fontWeight: '800',
    textAlign: 'right',
  },
  emptyHint: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 4,
  },
});
